import { NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { MenuItemType } from "../Types/marketing";

interface SidebarProps {
  menus: MenuItemType[];
}

interface MenuItemProps {
  menu: MenuItemType;
  level: number;
  openMenuIds: Set<number>;
  onOpenMenuChange: (menuIds: Set<number>) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ menus }) => {
  const [openMenuIds, setOpenMenuIds] = useState<Set<number>>(new Set());

  return (
    <aside className="h-full bg-white text-gray-800 overflow-y-auto border-r border-gray-200">
      <ul className="p-2 space-y-0.5">
        {menus
          .filter(menu => menu.permissions?.canAccess)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(menu => (
            <MenuItem
              key={menu.menuId}
              menu={menu}
              level={0}
              openMenuIds={openMenuIds}
              onOpenMenuChange={setOpenMenuIds}
            />
          ))}
      </ul>
    </aside>
  );
};

const MenuItem: React.FC<MenuItemProps> = ({
  menu,
  level,
  openMenuIds,
  onOpenMenuChange,
}) => {
  const location = useLocation();
  const hasChildren = !!menu.children?.length;

  /* ---------------------
     RECURSIVELY CHECK IF ANY NESTED CHILD IS ACTIVE
  --------------------- */
  const isAnyChildActive = (children: MenuItemType[]): boolean => {
    return children.some(child => {
      if (!child.routePath) {
        return child.children ? isAnyChildActive(child.children) : false;
      }

      const isCurrentActive =
        location.pathname === child.routePath ||
        location.pathname.startsWith(child.routePath + "/");

      if (isCurrentActive) return true;

      if (child.children) {
        return isAnyChildActive(child.children);
      }

      return false;
    });
  };

  const hasActiveChild = hasChildren && isAnyChildActive(menu.children!);

  const isDirectlyActive =
    !!menu.routePath &&
    (location.pathname === menu.routePath ||
      location.pathname.startsWith(menu.routePath + "/"));

  const isThisMenuOpen = openMenuIds.has(menu.menuId);

  /* ---------------------
     ✅ CLICK HANDLER - Toggle menu on click
  --------------------- */
  const handleToggleMenu = () => {
    if (!hasChildren) return;

    const newOpen = new Set(openMenuIds);
    if (newOpen.has(menu.menuId)) {
      newOpen.delete(menu.menuId);
    } else {
      newOpen.add(menu.menuId);
    }
    onOpenMenuChange(newOpen);
  };

  const getParentStyles = () => {
    const baseStyles = `
      flex items-center justify-between px-3 py-2 text-xs rounded
      transition-colors duration-200
    `;

    if (isDirectlyActive) {
      return `${baseStyles} bg-[#009A85] text-white font-semibold`;
    }

    if (hasActiveChild) {
      return `${baseStyles} bg-[#184A84] text-white font-semibold`;
    }
    return `${baseStyles} text-gray-700 hover:bg-slate-100 cursor-pointer`;
  };

  const getLevelPadding = (lv: number) => {
    const paddingMap: Record<number, string> = {
      0: "pl-3",
      1: "pl-3",
      2: "pl-3",
      3: "pl-3",
      4: "pl-3",
    };
    return paddingMap[lv] || `pl-${(lv + 1) * 3}`;
  };

  return (
    <li>
      {/* =====================
          MENU ITEM WITH ROUTE
      ===================== */}
      {menu.routePath ? (
        <NavLink
          to={menu.routePath}
          end
          className={({ isActive }) => `
            flex items-center justify-between px-3 py-2 text-xs rounded
            transition-colors duration-200
            ${isActive ? "bg-[#009A85] text-white font-semibold" : "text-gray-700 hover:bg-slate-100"}
          `}
        >
          <span className="flex-1 truncate">{menu.displayText}</span>
          {hasChildren && (
            <span
              onClick={(e) => {
                e.preventDefault(); // ✅ Prevent navigation when clicking arrow
                e.stopPropagation();
                handleToggleMenu();
              }}
              className={`text-[10px] ml-2 transition-transform duration-200 ${
                isThisMenuOpen ? "rotate-90" : ""
              }`}
            >
              ▶
            </span>
          )}
        </NavLink>
      ) : (
        /* =====================
           PARENT WITHOUT ROUTE - ✅ CLICK TO TOGGLE
        ===================== */
        <div
          onClick={handleToggleMenu}
          className={getParentStyles()}
        >
          <span className="flex-1 truncate">{menu.displayText}</span>
          {hasChildren && (
            <span
              className={`text-[10px] ml-2 transition-transform duration-200 ${
                isThisMenuOpen ? "rotate-90" : ""
              }`}
            >
              ▶
            </span>
          )}
        </div>
      )}

      {/* =====================
          CHILDREN (Nested recursively) - ✅ SHOWN ONLY WHEN CLICKED OPEN
      ===================== */}
      {hasChildren && isThisMenuOpen && (
        <ul className={`mt-0.5 space-y-0.5 border-l-2 border-slate-200 ${getLevelPadding(level)}`}>
          {menu.children!
            .filter(child => child.permissions?.canAccess)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(child => (
              <MenuItem
                key={child.menuId}
                menu={child}
                level={level + 1}
                openMenuIds={openMenuIds}
                onOpenMenuChange={onOpenMenuChange}
              />
            ))}
        </ul>
      )}
    </li>
  );
};

export default Sidebar;
